import React from 'react';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { ADMIN_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { useIsProductAdmin } from '@confluence/current-user/entry-points/useIsProductAdmin.ts';
import {
	PERFORMANCE_INSIGHTS_CPU_CORES,
	PERFORMANCE_INSIGHTS_NETWORK_SPEED,
	PERFORMANCE_INSIGHTS_NETWORK_PROTOCOL,
	PERFORMANCE_INSIGHTS_OPERATING_SYSTEM,
} from '@confluence/named-routes/entry-points/routes.ts';
import { RestrictedPage } from '@confluence/no-permission/entry-points/RestrictedPage.ts';
import { PerformanceInsightsPage } from '@confluence/performance-insights/entry-points/PerformanceInsightsPage.ts';

const USER_SETUP_DETAIL_ROUTE_NAMES = new Set([
	PERFORMANCE_INSIGHTS_NETWORK_SPEED.name,
	PERFORMANCE_INSIGHTS_CPU_CORES.name,
	PERFORMANCE_INSIGHTS_NETWORK_PROTOCOL.name,
	PERFORMANCE_INSIGHTS_OPERATING_SYSTEM.name,
]);

const resolveTab = ({
	legacyTab,
	routeName,
	tab,
}: {
	legacyTab: string | undefined;
	routeName: string;
	tab: string | undefined;
}): string => {
	if (USER_SETUP_DETAIL_ROUTE_NAMES.has(routeName) || tab === 'user-setup') {
		return 'user-setup';
	}
	if (tab === 'overview' || (tab === undefined && legacyTab === 'overview')) {
		return 'overview';
	}
	if (tab === 'content-metrics' || (tab === undefined && legacyTab === 'content-metrics')) {
		return 'content-metrics';
	}
	return 'load-times';
};

export const PerformanceInsightsRoute: {
	(): React.JSX.Element | null;
	NAVIGATION_PARAMS({ name, params: { tab } }: RouteMatch): {
		Screen: {
			screenEvent: {
				name: string;
				attributes: {
					tab: string;
				};
			};
			pageState: {
				routeName: string;
				tab: string;
			};
		};
		MainLayout: {
			navView: string;
		};
	};
} = (): React.JSX.Element | null => {
	const { isProductAdmin, isAdminCheckLoading } = useIsProductAdmin();

	if (isAdminCheckLoading) {
		return null;
	}

	return isProductAdmin ? <PerformanceInsightsPage /> : <RestrictedPage />;
};

PerformanceInsightsRoute.NAVIGATION_PARAMS = ({
	name,
	params: { tab },
	query,
}: RouteMatch): {
	Screen: {
		screenEvent: {
			name: string;
			attributes: {
				tab: string;
			};
		};
		pageState: {
			routeName: string;
			tab: string;
		};
	};
	MainLayout: {
		navView: string;
	};
} => {
	const legacyTab = Array.isArray(query.tab) ? query.tab[0] : query.tab;
	const resolvedTab = resolveTab({ legacyTab, routeName: name, tab });

	return {
		Screen: {
			screenEvent: {
				name: 'performanceInsightsScreen',
				attributes: {
					tab: resolvedTab,
				},
			},
			pageState: {
				routeName: name,
				tab: resolvedTab,
			},
		},
		MainLayout: {
			navView: ADMIN_HOME_ID,
		},
	};
};
